import React, { useEffect, useRef } from 'react';
import useAppSelector from 'App/components/hooks/useSelector';
import type { ExternalIntegration, NewWidget, Widget } from '../externalIntegrationTypes';
import { type Action, createLocalContextWithActionHandlers } from 'App/helpers/stateHelper';
import useExternalIntegrations from '../useExternalIntegrations';
import { RootState } from 'Store/index';

export const ACTIONS = {
	SET_INTEGRATION: 'SET_INTEGRATION',
	SET_FIELDS: 'SET_FIELDS',
	SET_WIDGETS: 'SET_WIDGETS',
	PENDING_WORKFLOW_FIELD_CHANGE: 'PENDING_WORKFLOW_FIELD_CHANGE'
};

const ACTION_HANDLERS: { [key: string]: (s: ExternalIntegrationState, a: Action) => ExternalIntegrationState } = {
	[ACTIONS.SET_INTEGRATION]: (state, action) => {
		return {
			...state,
			integration: action.payload
		};
	},
	[ACTIONS.SET_FIELDS]: (state, action) => {
		return {
			...state,
			fields: action.payload
		};
	},
	[ACTIONS.SET_WIDGETS]: (state, action) => {
		return {
			...state,
			widgets: action.payload
		};
	},
	[ACTIONS.PENDING_WORKFLOW_FIELD_CHANGE]: (state, action) => {
		return {
			...state,
			pendingWorkflowFieldChange: action.payload
		};
	}
};

export type ExternalIntegrationState = {
	integration: ExternalIntegration;
	fields?: any[];
	widgets?: Widget[];
	pendingWorkflowFieldChange?: boolean;
};

const { Provider, useSelector, useDispatch } = createLocalContextWithActionHandlers(ACTION_HANDLERS);

export const useExternalIntegrationSelector = useSelector;
export const useExternalIntegrationDispatch = useDispatch;

const ExternalIntegrationProvider = ({
	children,
	integration
}: {
	children: React.ReactNode;
	integration: ExternalIntegration;
}) => {
	const initialState: ExternalIntegrationState = {
		integration,
		fields: [],
		widgets: [],
		pendingWorkflowFieldChange: false
	};

	return <Provider value={initialState}>{children}</Provider>;
};

export const useExternalIntegration = () => {
	const integrationFromState = useExternalIntegrationSelector(state => state.integration);
	const integration = useAppSelector((state: RootState) => {
		const { allIntegrations } = state.ExternalIntegration;
		return allIntegrations.find(i => i.type === integrationFromState.type);
	});

	return integration || integrationFromState;
};

export const useExternalIntegrationFieldsActions = () => {
	const integration = useExternalIntegration();
	const widgets = useExternalIntegrationSelector(state => state.widgets);
	const dispatch = useExternalIntegrationDispatch();
	const pendingFieldFetch = useRef(false);
	const pendingWidgetFetch = useRef(false);
	const fieldsRef = useRef<any[]>([]);

	const {
		getFields,
		fetchIntegrations,
		changeFieldValue: originalChangeFieldValue,
		changeWorkflowFieldValue: originalChangeWorkflowFieldValue,
		getWidgets,
		saveWidget,
		deleteWidget
	} = useExternalIntegrations() as any;

	const externalIntegrationsHook = useExternalIntegrations() as any;

	useEffect(() => {
		if (integration && !pendingFieldFetch.current) {
			pendingFieldFetch.current = true;
			const fetchFields = async () => {
				const fields = await getFields(integration);
				const fieldsArray = [...fields];
				fieldsRef.current = fieldsArray;
				dispatch({
					type: ACTIONS.SET_FIELDS,
					payload: fieldsArray
				});
				pendingFieldFetch.current = false;
			};
			fetchFields();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [integration]);

	useEffect(() => {
		if (integration && !pendingWidgetFetch.current) {
			pendingWidgetFetch.current = true;
			const fetchWidgets = async () => {
				const widgets = await getWidgets(integration);
				dispatch({
					type: ACTIONS.SET_WIDGETS,
					payload: [...widgets]
				});
				pendingWidgetFetch.current = false;
			};
			fetchWidgets();
		}
	}, [integration]);

	const changeFieldValue = async (
		inputId: string,
		value: any,
		settings?: { selectedCredentialId?: string; selectedConfigurationId?: string }
	) => {
		await originalChangeFieldValue(integration.type, inputId, value, settings);
	};

	const changeFieldValues = async (
		inputId: string,
		value: any,
		settings?: { selectedCredentialId?: string; selectedConfigurationId?: string }
	) => {
		const currentValues = fieldsRef.current.reduce((acc, field) => {
			acc[field.id] = field.value === undefined ? field.storedValue : field.value;
			return acc;
		}, {} as any);

		const mergedValues = {
			...currentValues,
			[inputId]: value
		};

		const updatedFields = fieldsRef.current.map(field => (field.id === inputId ? { ...field, value } : field));
		fieldsRef.current = updatedFields;

		dispatch({
			type: ACTIONS.SET_FIELDS,
			payload: updatedFields
		});

		await externalIntegrationsHook.changeFieldValues(integration.type, mergedValues, settings);
	};

	const changeWorkflowFieldValue = async (
		integrationType: string,
		workflowId: string,
		inputId: string,
		value: any,
		settings?: { selectedCredentialId?: string; selectedConfigurationId?: string }
	) => {
		dispatch({
			type: ACTIONS.PENDING_WORKFLOW_FIELD_CHANGE,
			payload: true
		});

		await originalChangeWorkflowFieldValue(integrationType, workflowId, inputId, value, settings);
		const { availableIntegrations, connectedIntegrations } = await fetchIntegrations().promise;
		const all = [...connectedIntegrations, ...availableIntegrations];
		const updated = all.find(i => i.type === integrationType);
		if (updated) {
			dispatch({
				type: ACTIONS.SET_INTEGRATION,
				payload: JSON.parse(JSON.stringify(updated))
			});
		}
		dispatch({
			type: ACTIONS.PENDING_WORKFLOW_FIELD_CHANGE,
			payload: false
		});
	};

	const saveWidgetAction = async (newWidget: NewWidget | Widget) => {
		const widget = await saveWidget(newWidget);

		// Check if this is an update (widget has id) or new widget (no id in original)
		const isUpdate = 'id' in newWidget;

		if (isUpdate) {
			// Update existing widget
			const updatedWidgets = (widgets || []).map(w => (w.id === widget.id ? widget : w));
			dispatch({
				type: ACTIONS.SET_WIDGETS,
				payload: updatedWidgets
			});
		} else {
			// Add new widget
			dispatch({
				type: ACTIONS.SET_WIDGETS,
				payload: [...(widgets || []), widget]
			});
		}
	};

	const deleteWidgetAction = async (widgetId: number) => {
		await deleteWidget(widgetId);
		const newWidgets = [...(widgets || [])].filter(w => w.id !== widgetId);
		dispatch({
			type: ACTIONS.SET_WIDGETS,
			payload: newWidgets
		});
	};

	return {
		changeFieldValue,
		changeFieldValues,
		changeWorkflowFieldValue,
		saveWidget: saveWidgetAction,
		deleteWidget: deleteWidgetAction
	};
};

export const useExternalIntegrationActions = () => {
	const dispatch = useExternalIntegrationDispatch();

	const {
		saveWorkflowErrorHandlings,
		saveGlobalSettings: originalSaveGlobalSettings,
		getFields,
		getWorkflowFields,
		getWorkflowSettings,
		fetchIntegrations,
		changeWorkflowFieldValue: originalChangeWorkflowFieldValue,
		updateWorkflowEnabled: originalUpdateWorkflowEnabled,
		saveWidget,
		deleteWidget
	} = useExternalIntegrations();

	const saveGlobalSettings = async (
		integration: ExternalIntegration,
		settings: {
			id?: number;
			multiAccount: boolean;
			userOnly: boolean;
			healthCheckEndpoint?: string;
			initSyncWorkflowId?: string;
		}
	) => {
		await originalSaveGlobalSettings(integration, settings);

		// Refetch integrations and update the context state
		const { availableIntegrations, connectedIntegrations } = await fetchIntegrations().promise;
		const all = [...connectedIntegrations, ...availableIntegrations];
		const updated = all.find(i => i.type === integration.type);
		if (updated) {
			dispatch({
				type: ACTIONS.SET_INTEGRATION,
				payload: JSON.parse(JSON.stringify(updated))
			});
		}
	};

	const updateWorkflowEnabled = async (integration: ExternalIntegration, workflowId: string, enabled: boolean) => {
		const result = await originalUpdateWorkflowEnabled(integration, workflowId, enabled);

		// Update the context state with the updated integration
		if (result.updatedIntegration) {
			dispatch({
				type: ACTIONS.SET_INTEGRATION,
				payload: JSON.parse(JSON.stringify(result.updatedIntegration))
			});
		}

		return result;
	};

	return {
		saveWorkflowErrorHandlings,
		saveGlobalSettings,
		getFields,
		getWorkflowFields,
		getWorkflowSettings,
		fetchIntegrations,
		updateWorkflowEnabled,
		changeWorkflowFieldValue: originalChangeWorkflowFieldValue,
		saveWidget,
		deleteWidget
	};
};

export default ExternalIntegrationProvider;
